import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Upload, Image as ImageIcon, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminSession } from "@/hooks/use-admin-session";
import {
  getMyStaffProfile,
  updateMyStaffProfile,
  type StaffProfile,
} from "@/lib/staff.functions";

export const Route = createFileRoute("/_admin/admin/profile")({
  head: () => ({
    meta: [
      { title: "Mein Profil – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MyProfilePage,
});

const STAFF_BUCKET = "staff-photos";
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

function MyProfilePage() {
  const session = useAdminSession();
  const queryClient = useQueryClient();
  const loadFn = useServerFn(getMyStaffProfile);
  const saveFn = useServerFn(updateMyStaffProfile);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-staff-profile"],
    queryFn: () => loadFn(),
    enabled: session.status === "authenticated",
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!data) return;
    setFirstName(data.firstName ?? "");
    setLastName(data.lastName ?? "");
    setPhone(data.phone ?? "");
    setJobTitle(data.jobTitle ?? "");
    setPhotoPath(data.photoPath ?? null);
    setPhotoUrl(data.photoUrl ?? null);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: {
      first_name: string;
      last_name: string;
      phone: string | null;
      job_title: string | null;
      photo_path: string | null;
    }) => saveFn({ data: input }),
    onSuccess: (result) => {
      toast.success("Profil aktualisiert.");
      if (result) {
        queryClient.setQueryData(["my-staff-profile"], result);
      }
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Speichern fehlgeschlagen.");
    },
  });

  async function handleFile(file: File) {
    if (!session.userId) return;
    if (file.size > MAX_BYTES) {
      toast.error("Datei ist zu groß (max. 5 MB).");
      return;
    }
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Format nicht erlaubt. Bitte JPG, PNG oder WebP.");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `${session.userId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from(STAFF_BUCKET)
        .upload(path, file, {
          cacheControl: "3600",
          contentType: file.type || undefined,
          upsert: false,
        });
      if (error) throw error;
      // Alte Datei entfernen
      if (photoPath) {
        await supabase.storage.from(STAFF_BUCKET).remove([photoPath]);
      }
      const { data: signed } = await supabase.storage
        .from(STAFF_BUCKET)
        .createSignedUrl(path, 60 * 15);
      setPhotoPath(path);
      setPhotoUrl(signed?.signedUrl ?? null);
      toast.success("Foto hochgeladen. Nicht vergessen zu speichern.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  async function removePhoto() {
    if (photoPath) {
      await supabase.storage.from(STAFF_BUCKET).remove([photoPath]);
    }
    setPhotoPath(null);
    setPhotoUrl(null);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim() || null,
      job_title: jobTitle.trim() || null,
      photo_path: photoPath,
    });
  }

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Profil wird geladen …</div>;
  }
  if (isError || !data) {
    return (
      <div className="text-sm text-destructive">
        Profil konnte nicht geladen werden.
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary md:text-4xl">
          Mein Profil
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Pflegen Sie hier Ihre Kontaktdaten und Ihr Profilbild. Die
          E-Mail-Adresse und Ihre Rolle können nur Administratoren ändern.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-8">
        <section className="rounded-lg border border-border p-5">
          <h2 className="font-display text-base font-extrabold">Foto</h2>
          <div className="mt-4 flex items-center gap-5">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary">
              {uploading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : photoUrl ? (
                <img
                  src={photoUrl}
                  alt={data.fullName ?? "Profilbild"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                  e.currentTarget.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
              >
                <Upload className="h-3.5 w-3.5" />
                {photoPath ? "Foto ersetzen" : "Foto hochladen"}
              </button>
              {photoPath && (
                <button
                  type="button"
                  onClick={removePhoto}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold text-destructive hover:bg-secondary"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Entfernen
                </button>
              )}
              <p className="text-xs text-muted-foreground">
                JPG, PNG oder WebP, max. 5 MB.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border p-5">
          <h2 className="font-display text-base font-extrabold">Kontaktdaten</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" required>
              <input
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputCls}
                maxLength={80}
              />
            </Field>
            <Field label="Nachname" required>
              <input
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputCls}
                maxLength={80}
              />
            </Field>
            <Field label="Funktionsbezeichnung">
              <input
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="z. B. Senior Campaign Strategist"
                className={inputCls}
                maxLength={80}
              />
            </Field>
            <Field label="Telefonnummer">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49 30 82686000"
                className={inputCls}
                maxLength={40}
              />
            </Field>
            <Field label="E-Mail (nur Anzeige)">
              <input
                disabled
                value={data.email ?? ""}
                className={`${inputCls} bg-secondary/50 text-muted-foreground`}
              />
            </Field>
            <Field label="Rolle (nur Anzeige)">
              <input
                disabled
                value={data.role === "admin" ? "Administrator" : data.role === "editor" ? "Editor" : "—"}
                className={`${inputCls} bg-secondary/50 text-muted-foreground`}
              />
            </Field>
          </div>
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending || uploading}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-5 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {mutation.isPending ? "Speichern …" : "Änderungen speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? " *" : ""}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

// Type import for adminSession helper (only for TS)
type _KeepProfileType = StaffProfile;
